import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { API } from '../utils';
import { toast } from 'sonner';
import { Camera, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      const message = res.data?.message || '';
      const googleAccount = message.toLowerCase().includes('google');
      setResponseMessage(message);
      setIsGoogleAccount(googleAccount);
      setSubmitted(true);
    } catch (err) {
      setResponseMessage('If an account exists with this email, you will receive a password reset link.');
      setIsGoogleAccount(false);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <div className={`w-16 h-16 ${isGoogleAccount ? 'bg-blue-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <CheckCircle className={`w-8 h-8 ${isGoogleAccount ? 'text-blue-600' : 'text-green-600'}`} />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {isGoogleAccount ? 'Sign in with Google' : 'Check Your Email'}
            </h1>
            <p className="text-gray-600 mb-6">
              {responseMessage}
            </p>
            {!isGoogleAccount && (
              <p className="text-sm text-gray-500 mb-6">
                The link will expire in 1 hour.
              </p>
            )}
            <div className="space-y-3">
              <Button
                onClick={() => { setSubmitted(false); setResponseMessage(''); setIsGoogleAccount(false); }}
                variant="outline"
                className="w-full rounded-full"
              >
                Try Another Email
              </Button>
              <Link to="/login">
                <Button className="w-full rounded-full bg-black text-white hover:bg-gray-900">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Camera className="w-8 h-8" />
            <span className="text-2xl font-bold tracking-tight">FrameFinder</span>
          </Link>
          <h1 className="text-3xl font-bold mb-2">Forgot Password?</h1>
          <p className="text-gray-600">Enter your email and we'll send you a reset link</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-10 h-12 rounded-xl border-gray-200"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-black text-white hover:bg-gray-900"
              data-testid="send-reset-link-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
